import type { ColumnWidths } from "./EntriesTable";

const COLUMN_MIN_WIDTHS: ColumnWidths = { date: 5, matter: 8, codes: 6, narrative: 20, units: 6 };
const COLUMN_ORDER: (keyof ColumnWidths)[] = ["date", "matter", "codes", "narrative"];

/**
 * Distribute a column-width delta proportionally across the columns to the right.
 * Pure function — call it as `setColumnWidths(prev => cascadeColumnWidths(prev, col, delta))`.
 * Ported 1:1 from TrackedView.tsx (`handleCascadingResize`).
 *
 * Expansion: takes width from right-hand columns proportional to slack above their min widths.
 * Shrinkage: gives the reclaimed space to the immediately-following column only.
 */
export function cascadeColumnWidths(
  current: ColumnWidths,
  resizingColumn: keyof ColumnWidths,
  deltaPercentage: number,
): ColumnWidths {
  const currentIndex = COLUMN_ORDER.indexOf(resizingColumn);
  if (currentIndex === -1) return current;

  const newWidths: ColumnWidths = { ...current };
  const currentWidth = current[resizingColumn];
  const minWidth = COLUMN_MIN_WIDTHS[resizingColumn];

  if (deltaPercentage > 0) {
    let availableSpace = 0;
    for (let i = currentIndex + 1; i < COLUMN_ORDER.length; i++) {
      const key = COLUMN_ORDER[i];
      availableSpace += Math.max(0, current[key] - COLUMN_MIN_WIDTHS[key]);
    }
    const actualExpansion = Math.min(deltaPercentage, availableSpace);
    if (actualExpansion <= 0) return current;
    newWidths[resizingColumn] = currentWidth + actualExpansion;
    let remaining = actualExpansion;
    for (let i = currentIndex + 1; i < COLUMN_ORDER.length && remaining > 0.01; i++) {
      const key = COLUMN_ORDER[i];
      const colWidth = current[key];
      const colMin = COLUMN_MIN_WIDTHS[key];
      const canRemove = Math.max(0, colWidth - colMin);
      const toRemove = Math.min(remaining, canRemove);
      if (toRemove > 0.01) {
        newWidths[key] = colWidth - toRemove;
        remaining -= toRemove;
      }
    }
  } else {
    const maxShrinkage = currentWidth - minWidth;
    const actualShrinkage = Math.max(deltaPercentage, -maxShrinkage);
    if (actualShrinkage >= 0) return current;
    newWidths[resizingColumn] = currentWidth + actualShrinkage;
    const spaceToGive = -actualShrinkage;
    const nextKey = COLUMN_ORDER[currentIndex + 1];
    if (nextKey !== undefined) {
      newWidths[nextKey] = current[nextKey] + spaceToGive;
    }
  }

  return newWidths;
}
